import { useEffect, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { ExpandMoreIcon, SectionCard, StatusChip, SsoIcon } from '@platform/ui-kit';
import { HelpPanel, type HelpContent } from '../portfolio_definition/HelpPanel';
import { api, type SsoConfig } from '../../api';
import { describeError, useAsyncData } from './useAsyncData';

/**
 * Screen 4 — SSO / identity (spec §C).
 *
 * Microsoft Entra ID and Google Workspace sign-in are brokered through this
 * deployment's own AWS Cognito User Pool — the IdP credentials themselves are
 * shared/Talogent-owned (not entered here); this screen only sets which IdPs are on
 * and which tenant/domain they're restricted to. The original generic OIDC/SAML
 * fields below that are no longer wired to anything live under "Legacy" — kept
 * rather than removed (CLAUDE.md §1.8), since deleting a persisted, previously-live
 * config screen wasn't asked for.
 */
/**
 * The drawer's first job is to separate the TWO CONFIGURATION LAYERS, because conflating
 * them is the documented trap on this screen (CLAUDE.md, 2026-08-08).
 *
 *   Layer 1 — the deployment's own auth mode. Decides whether SSO is possible at all.
 *             Not on this screen, not editable by an administrator.
 *   Layer 2 — the provider toggles here, stored in `platform.sso_config`. Decide which
 *             buttons the sign-in screen offers. Live, no restart.
 *
 * Turning a toggle on does NOT turn SSO on, and the screen cannot say so by itself —
 * which is exactly why it belongs in the drawer rather than being left to be discovered.
 */
const SSO_HELP: HelpContent = {
  title: 'SSO / Identity',
  icon: '🔐',
  purpose:
    'Decides <strong>which identity providers this deployment&rsquo;s users can sign in with</strong>, and what happens to someone arriving from one for the first time. Microsoft Entra ID and Google Workspace are brokered through this deployment&rsquo;s own AWS Cognito user pool.',
  doHere: [
    'Turn <strong>Microsoft Entra ID</strong> sign-in on or off, and restrict it to one Entra tenant.',
    'Turn <strong>Google Workspace</strong> sign-in on or off, and restrict it to one Workspace domain.',
    'Decide whether <strong>local password sign-in</strong> stays available alongside SSO.',
    'Decide whether a first-time SSO arrival is <strong>given an account automatically</strong>, and what role that account gets.',
    'Save with <strong>Save configuration</strong>. Changes take effect immediately &mdash; no restart.',
  ],
  fits: 'Administration &rarr; SSO / Identity',
  firstTime:
    'Leave <strong>Allow local password sign-in</strong> switched on until you have signed in through the provider yourself at least once. It is the way back in if the provider is misconfigured, and switching it off before testing is how an install locks everybody out.',
  sections: [
    {
      type: 'html',
      label: 'Two separate layers — this is the thing to understand first',
      html: 'A toggle here is <strong>not</strong> the switch that turns SSO on. There are two independent layers, and both must be in place. <strong>First</strong>, the deployment&rsquo;s own authentication mode, set in its environment, decides whether SSO is possible at all; it is not on this screen and an administrator cannot change it. <strong>Second</strong>, these toggles decide which providers the sign-in screen offers. Switching a provider on while the deployment is not running in SSO mode changes a stored setting and nothing visible &mdash; and the reverse is equally true: a deployment in SSO mode shows <em>no</em> provider buttons until a toggle here is on.',
    },
    {
      type: 'html',
      label: 'And a third thing has to exist: the Cognito pool',
      html: 'Microsoft and Google sign-in are not talked to directly &mdash; they are <strong>brokered through this deployment&rsquo;s own AWS Cognito user pool</strong>, and the provider credentials themselves are Talogent-managed rather than entered here. <strong>Until that pool has been provisioned for this deployment, enabling a toggle has no effect</strong> and local accounts remain the only working path. That is why the banner at the top of the screen says so: it is the most common reason a correctly-configured toggle appears to do nothing.',
    },
    {
      type: 'defs',
      label: 'What each setting does',
      items: [
        {
          term: 'Entra ID tenant ID',
          def: 'Restricts sign-in to <strong>one</strong> Microsoft organisation. Left blank, any Entra tenant can sign in &mdash; which is almost never what a single-client install wants.',
        },
        {
          term: 'Google Workspace domain',
          def: 'Restricts sign-in to one Workspace domain. Left blank, <strong>any personal Google account</strong> can sign in, not just the client&rsquo;s staff.',
        },
        {
          term: 'Allow local password sign-in alongside SSO',
          def: 'Keeps the username-and-password form available. Your way back in if the provider breaks; also the path Talogent support uses.',
        },
        {
          term: 'Create a user automatically on first sign-in',
          def: 'Off, a person must already exist here before the provider will let them in. On, an account is created for them the first time they arrive &mdash; so the restriction fields above become the thing standing between the provider and new accounts in this install.',
        },
        {
          term: 'Default role for provisioned users',
          def: 'The role an automatically-created account is given. <strong>Standard User</strong> unless there is a specific reason otherwise &mdash; a Client Administrator is assigned to every licensed module automatically.',
        },
      ],
      note:
        'Leaving a restriction field blank is a decision, not a default: it widens who may sign in rather than narrowing it.',
    },
    {
      type: 'html',
      label: 'Where the email domains come from',
      html: 'Blank <strong>accepted email domains</strong> fall back to the ones recorded under <em>Administration &rarr; Company Profile</em>. Keeping them in one place there, rather than re-typing them here, means a client adding a domain updates both at once.',
    },
    {
      type: 'html',
      label: 'The Legacy section, and why it is still there',
      html: 'The collapsed <strong>Legacy (unused)</strong> block holds generic OIDC and SAML fields &mdash; issuer URL, client ID, client secret, redirect URI &mdash; that predate Cognito and are <strong>no longer read by the sign-in flow</strong>. They are kept rather than deleted because their stored values may still be referenced, and removing a previously-live configuration screen was never asked for. Editing them changes nothing about how anybody signs in today. The client secret, if one is stored, is <strong>encrypted at rest and never returned to the browser</strong>, so it can be replaced but not read.',
    },
  ],
};

export function SsoTab() {
  const { data, loading, error, reload } = useAsyncData<SsoConfig>(() => api.admin.getSso());
  const [form, setForm] = useState<SsoConfig>();
  const [secret, setSecret] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string>();
  const [saved, setSaved] = useState(false);

  useEffect(() => setForm(data), [data]);

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!form) return null;

  const set = <K extends keyof SsoConfig>(key: K, value: SsoConfig[K]) => {
    setForm({ ...form, [key]: value });
    setSaved(false);
  };

  const save = async () => {
    setSaving(true);
    setSaveError(undefined);
    try {
      await api.admin.updateSso({
        enabled: form.enabled,
        protocol: form.protocol,
        issuerUrl: form.issuer_url,
        clientId: form.client_id,
        ...(secret ? { clientSecret: secret } : {}),
        redirectUri: form.redirect_uri,
        emailDomains: form.email_domains,
        allowLocalFallback: form.allow_local_fallback,
        autoProvisionUsers: form.auto_provision_users,
        defaultRole: form.default_role,
        googleEnabled: form.google_enabled,
        googleDomain: form.google_domain,
        entraEnabled: form.entra_enabled,
        entraTenantId: form.entra_tenant_id,
      });
      setSecret('');
      setSaved(true);
      reload();
    } catch (err) {
      setSaveError(describeError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2}>
      <HelpPanel help={SSO_HELP} />

      <Alert severity="info">
        Microsoft and Google sign-in are brokered through this deployment&apos;s own AWS Cognito
        User Pool. Enabling a toggle here has no effect until that Cognito pool has been
        provisioned — local accounts remain the working path until then.
      </Alert>

      <SectionCard
        title={
          <Stack direction="row" spacing={1} alignItems="center">
            <SsoIcon fontSize="small" />
            <span>Single sign-on</span>
          </Stack>
        }
        subheader="Identity providers authenticate this deployment's users; local accounts remain as fallback."
      >
        <Stack spacing={2.5} sx={{ maxWidth: 640 }}>
          {saveError ? <Alert severity="error">{saveError}</Alert> : null}
          {saved ? <Alert severity="success">SSO configuration saved.</Alert> : null}

          <Stack spacing={1}>
            <Typography variant="subtitle2">Microsoft Entra ID</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={form.entra_enabled}
                  onChange={(e) => set('entra_enabled', e.target.checked)}
                />
              }
              label="Enable Microsoft Entra ID sign-in"
            />
            {form.entra_enabled ? (
              <TextField
                label="Entra ID tenant ID"
                value={form.entra_tenant_id ?? ''}
                onChange={(e) => set('entra_tenant_id', e.target.value || null)}
                fullWidth
                helperText="Restrict sign-in to this Entra ID tenant. Leave blank to allow any organization."
              />
            ) : null}
          </Stack>

          <Stack spacing={1}>
            <Typography variant="subtitle2">Google Workspace</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={form.google_enabled}
                  onChange={(e) => set('google_enabled', e.target.checked)}
                />
              }
              label="Enable Google Workspace sign-in"
            />
            {form.google_enabled ? (
              <TextField
                label="Google Workspace domain"
                value={form.google_domain ?? ''}
                onChange={(e) => set('google_domain', e.target.value || null)}
                fullWidth
                helperText="Restrict sign-in to this Workspace domain. Leave blank to allow any Google account."
              />
            ) : null}
          </Stack>

          <FormControlLabel
            control={
              <Switch
                checked={form.allow_local_fallback}
                onChange={(e) => set('allow_local_fallback', e.target.checked)}
              />
            }
            label="Allow local password sign-in alongside SSO"
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.auto_provision_users}
                onChange={(e) => set('auto_provision_users', e.target.checked)}
              />
            }
            label="Create a user automatically on first successful IdP sign-in"
          />

          {form.auto_provision_users ? (
            <TextField
              label="Default role for provisioned users"
              select
              value={form.default_role}
              onChange={(e) => set('default_role', e.target.value)}
              sx={{ maxWidth: 320 }}
            >
              <MenuItem value="Standard User">Standard User</MenuItem>
              <MenuItem value="Client Administrator">Client Administrator</MenuItem>
            </TextField>
          ) : null}

          <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" color="text.secondary">
                Legacy (unused)
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2.5}>
                <Typography variant="body2" color="text.secondary">
                  These fields predate Cognito and are no longer read by the sign-in flow — Entra
                  ID and Google now run through the toggles above with shared, Talogent-managed
                  credentials. Kept rather than removed in case anything still depends on their
                  stored values.
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={form.enabled}
                      onChange={(e) => set('enabled', e.target.checked)}
                    />
                  }
                  label="Enable single sign-on"
                />

                <TextField
                  label="Protocol"
                  select
                  value={form.protocol}
                  onChange={(e) => set('protocol', e.target.value)}
                  sx={{ maxWidth: 220 }}
                >
                  <MenuItem value="oidc">OIDC</MenuItem>
                  <MenuItem value="saml">SAML</MenuItem>
                </TextField>

                <TextField
                  label="Issuer URL"
                  value={form.issuer_url ?? ''}
                  onChange={(e) => set('issuer_url', e.target.value || null)}
                  fullWidth
                />
                <TextField
                  label="Client ID"
                  value={form.client_id ?? ''}
                  onChange={(e) => set('client_id', e.target.value || null)}
                  fullWidth
                />

                <Box>
                  <TextField
                    label="Client secret"
                    type="password"
                    value={secret}
                    onChange={(e) => setSecret(e.target.value)}
                    fullWidth
                    placeholder={form.has_client_secret ? 'Stored — leave blank to keep' : ''}
                    helperText="Encrypted at rest with AES-256-GCM. Never returned to the browser."
                  />
                  <Box sx={{ mt: 1 }}>
                    <StatusChip
                      tone={form.has_client_secret ? 'success' : 'neutral'}
                      label={form.has_client_secret ? 'Secret stored' : 'No secret stored'}
                    />
                  </Box>
                </Box>

                <TextField
                  label="Redirect URI"
                  value={form.redirect_uri ?? ''}
                  onChange={(e) => set('redirect_uri', e.target.value || null)}
                  fullWidth
                />
                <TextField
                  label="Accepted email domains"
                  value={form.email_domains.join(', ')}
                  onChange={(e) =>
                    set(
                      'email_domains',
                      e.target.value.split(',').map((d) => d.trim()).filter(Boolean),
                    )
                  }
                  fullWidth
                  helperText="Comma-separated. Blank falls back to the company profile's domains."
                />
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Stack direction="row" spacing={1} alignItems="center">
            <Button variant="contained" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save configuration'}
            </Button>
            {data?.updated_at ? (
              <Typography variant="caption" color="text.secondary">
                Last updated {new Date(data.updated_at).toLocaleString()}
              </Typography>
            ) : null}
          </Stack>
        </Stack>
      </SectionCard>
    </Stack>
  );
}
